'use client'

import {
  PageContainer,
  PageHeader,
  PageHeaderLeading,
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
  Button,
  EntityAboutSection,
  Textarea,
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  EntityDetailSkeleton,
  useRetryFitAnalysis,
  isMarketplaceApplyCaptureEnabled,
  listingProviderDetailHint,
  navigateWithThreadPreservation,
  resolveListingProviderFromOpportunity,
} from '@yeshuman/sdk'
import { useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { FileText } from 'lucide-react'

import { CandidateJobDetailHeaderActions } from './candidate-job-detail-header-actions'
import { CandidateJobFitSummaryWidget } from './candidate-job-fit-summary-widget'
import { MarketplaceApplyRedirectStatus } from './marketplace-apply-redirect-status'
import {
  JobOpeningIdentityHeader,
  JobOpeningMetaLine,
  JobOpeningSalaryFooter,
  parseJobOpeningMetaFields,
} from '../job-opening-identity'
import { useMarketplaceOpportunity } from '../../hooks/use-marketplace-opportunities'
import { useMarketplaceOutboundApply } from '../../hooks/use-marketplace-outbound-apply'
import { useSavedOpportunities } from '../../hooks/use-saved-opportunities'
import { useCandidateJobsBackTarget } from '../../hooks/use-navigate-to-candidate-job-detail'
import type { OpportunityJobSummaryInput } from '../../utils/opportunity-job-meta'

function marketplaceShareUrl(opportunityId: string): string {
  if (typeof window === 'undefined') {
    return `/candidate/jobs/marketplace/${encodeURIComponent(opportunityId)}`
  }
  return `${window.location.origin}/candidate/jobs/marketplace/${encodeURIComponent(opportunityId)}`
}

function boardShortName(job: {
  provider?: string
  attribution_label?: string
  attribution_url?: string
} | null): string {
  if (!job) return 'board'
  const listingProvider = resolveListingProviderFromOpportunity(job) ?? {
    key: job.provider || 'marketplace',
    label: job.attribution_label || 'Marketplace listing',
  }
  return (
    listingProvider.label?.replace(/^Jobs by\s+/i, '') ||
    listingProvider.key ||
    'board'
  ).trim()
}

export function CandidateMarketplaceJobDetailPage() {
  const { id = '' } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { data: job, isLoading, error } = useMarketplaceOpportunity(id)
  const {
    savedIds,
    savedOpportunities,
    pendingOpportunityIds,
    stuckOpportunityIds,
    toggleSave,
    isSaving,
    savingOpportunityId,
  } = useSavedOpportunities()
  const { retryCandidate, mutatingOpportunityId } = useRetryFitAnalysis()
  const [note, setNote] = useState('')
  const [alphaDialogOpen, setAlphaDialogOpen] = useState(false)
  const jobsBack = useCandidateJobsBackTarget()
  const captureApply = isMarketplaceApplyCaptureEnabled()

  const expired = job?.marketplace_status === 'expired'
  const providerShortName = boardShortName(job ?? null)
  const outboundApply = useMarketplaceOutboundApply({
    job: job ?? null,
    boardName: providerShortName,
    note,
    captureApply,
    expired: Boolean(expired),
    onAlphaCaptured: () => setAlphaDialogOpen(true),
  })

  const jobMeta: OpportunityJobSummaryInput | null = useMemo(() => {
    if (!job) return null
    return {
      employment_type: job.employment_type,
      work_mode: job.work_mode,
      location: job.location,
      salary_visibility: job.salary_visibility,
      salary_min: job.salary_min,
      salary_max: job.salary_max,
      salary_currency: job.salary_currency,
      salary_period: job.salary_period,
    }
  }, [job])

  if (isLoading) return <EntityDetailSkeleton variant="job" ariaLabel="Loading job" />
  if (error || !job) {
    return (
      <PageContainer>
        <PageHeader leading={<PageHeaderLeading back={jobsBack} />} />
        <p className="text-sm text-muted-foreground">Listing not found.</p>
      </PageContainer>
    )
  }

  const saved = savedIds.has(job.id)
  const listingProvider = resolveListingProviderFromOpportunity(job) ?? {
    key: job.provider || 'marketplace',
    label: job.attribution_label || 'Marketplace listing',
    href: job.attribution_url || undefined,
  }
  const savedRow = savedOpportunities.find((row) => row.id === job.id) ?? null
  // Fit analysis for marketplace is only shown when a save already triggered it (cheap path).
  const showFitWidget = Boolean(savedRow || savedIds.has(job.id))
  const fitStuck = stuckOpportunityIds.has(job.id)
  const fitPending =
    pendingOpportunityIds.has(job.id) || (!fitStuck && savedIds.has(job.id) && !savedRow)
  const { salaryText: salaryFromMeta } = parseJobOpeningMetaFields(jobMeta)
  const salaryLabel = salaryFromMeta || ''
  const shareUrl = marketplaceShareUrl(job.id)
  const companyLabel = job.company_display_name || job.organisation_name || ''
  const providerHint = listingProviderDetailHint(listingProvider.key)

  return (
    <PageContainer>
      <PageHeader
        leading={<PageHeaderLeading back={jobsBack} />}
        actions={
          <CandidateJobDetailHeaderActions
            shareUrl={shareUrl}
            application={null}
            saved={saved}
            isSaving={isSaving && savingOpportunityId === job.id}
            onSaveToggle={() => void toggleSave(job.id, saved)}
            onApply={() => void outboundApply.applyViaBoard()}
            busy={outboundApply.busy}
            applyLabel={outboundApply.applyLabel}
            applyExternal={outboundApply.applyExternal}
            applyDisabled={outboundApply.applyDisabled}
          />
        }
      />

      <div className="space-y-6">
        <Card>
          <CardContent className="space-y-3">
            <JobOpeningIdentityHeader
              presentation="detail"
              title={job.title}
              showPublicationBadge={false}
              organisationLabel={companyLabel}
              listingProvider={{
                key: listingProvider.key,
                label: job.attribution_label || listingProvider.label,
                logoUrl: listingProvider.logoUrl,
                href: job.attribution_url || listingProvider.href,
              }}
            />
            <JobOpeningMetaLine
              meta={jobMeta}
              publishedAt={job.published_at}
              updatedAt={job.updated_at}
              timestampVariant="interactive"
            />
            {providerHint ? (
              <p className="text-xs text-muted-foreground">{providerHint}</p>
            ) : null}
          </CardContent>
          {salaryLabel ? (
            <CardFooter className="border-t border-border/60">
              <JobOpeningSalaryFooter meta={jobMeta} salaryLabel={salaryLabel} />
            </CardFooter>
          ) : null}
        </Card>

        {showFitWidget ? (
          <CandidateJobFitSummaryWidget
            insight={savedRow?.insight ?? null}
            pending={fitPending}
            stuck={fitStuck}
            onRetry={() => retryCandidate(job.id)}
            retryPending={mutatingOpportunityId === job.id}
          />
        ) : null}

        {!expired ? (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Express interest</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <label className="text-sm font-medium" htmlFor="interest-note">
                Optional note (saved when you Express interest or Apply)
              </label>
              <Textarea
                id="interest-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Why this role interests you (saved to your TalentCo profile activity)"
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                Apply via {providerShortName} opens the board right away, and records your interest
                in the background.
              </p>
              <MarketplaceApplyRedirectStatus
                message={outboundApply.redirectStatus}
                boardName={providerShortName}
                blockedOpenUrl={outboundApply.blockedOpenUrl}
                onOpenInSameTab={outboundApply.openBoardInSameTab}
              />
              {outboundApply.interestSaved && !outboundApply.redirecting ? (
                <p className="text-xs text-muted-foreground" role="status">
                  {captureApply
                    ? `Interest saved. External apply via ${providerShortName} is paused during alpha.`
                    : 'Interest saved. You can apply when ready.'}
                </p>
              ) : null}
            </CardContent>
            <CardFooter className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={outboundApply.submitting || savingOpportunityId === job.id}
                onClick={() => void outboundApply.expressInterest()}
              >
                {outboundApply.interestSaved ? 'Interest saved' : 'Express interest'}
              </Button>
            </CardFooter>
          </Card>
        ) : (
          <Card>
            <CardContent>
              <p className="text-sm text-destructive">This listing may no longer be available.</p>
              <Button
                type="button"
                variant="outline"
                className="mt-3"
                onClick={() =>
                  navigateWithThreadPreservation(navigate, '/candidate/jobs', searchParams)
                }
              >
                Browse jobs
              </Button>
            </CardContent>
          </Card>
        )}

        <EntityAboutSection
          title="Job description"
          leadingIcon={FileText}
          subtitle="Full text for this role"
          body={job.description}
          stripLeadingTitles={[job.title]}
          emptyVariant="text"
          emptyMessage="No description provided."
        />
      </div>

      <AlertDialog open={alphaDialogOpen} onOpenChange={setAlphaDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Alpha testing</AlertDialogTitle>
            <AlertDialogDescription>
              We&apos;ve recorded your interest in this role. Applying via {providerShortName} is
              paused while TalentCo is in alpha testing. You can still apply normally to
              TalentCo-hosted jobs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction>Got it</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  )
}
